import { GridApi, GridRowId, GridRowModes, GridRowModesModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { MutableRefObject, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

type useGridApiRowModelActionsProps = {
  apiRef: MutableRefObject<GridApi>;
  scrollIntoView?: boolean; // enforces new element added at the end is scrolled into view
  rowFactory?: () => GridValidRowModel; // allows to specify custom default values for new rows
};

export const useGridApiRowModelActions = ({
  apiRef,
  scrollIntoView,
  rowFactory = () => ({ id: uuidv4() }), // Fallback to create at least an id if no rowFactory was provided
}: useGridApiRowModelActionsProps) => {
  // Row Edit Mode State hanlding
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const updateRowMode = (mode: GridRowModes, id: GridRowId) => {
    setRowModesModel((prevRowModesModel) => ({
      ...prevRowModesModel,
      [id]: { mode },
    }));
  };

  // Ensure changes are *NOT* forwarded to processRowUpdate() of data grid
  const cancelRowMode = (id: GridRowId) => {
    setRowModesModel((prevRowModesModel) => ({
      ...prevRowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
  };

  // Helper functions
  const getRow = (id: GridRowId) => apiRef.current.getRowModels().get(id);

  const isNewRow = (id: GridRowId) => getRow(id)?._isNew;

  const addIsNewFlag = (row: GridValidRowModel): GridValidRowModel => {
    const newRow = { ...row, _isNew: true };
    apiRef.current.updateRows([newRow]);
    return newRow;
  };

  const removeIsNewFlag = (id: GridRowId) => {
    const row = getRow(id);
    if (row) {
      apiRef.current.updateRows([{ ...row, _isNew: undefined }]);
    }
  };

  const removeRow = (id: GridRowId) => apiRef.current.updateRows([{ id, _action: 'delete' }]);

  // Common Grid Row Actions
  const addRow = () => {
    const newRow = rowFactory();
    updateRowMode(GridRowModes.Edit, addIsNewFlag(newRow).id);

    if (scrollIntoView) {
      setTimeout(() => apiRef.current.scrollToIndexes({ rowIndex: apiRef.current.getRowsCount() - 1 }), 10);
    }
    return newRow;
  };

  const editRow = (id: GridRowId) => updateRowMode(GridRowModes.Edit, id);

  const cancelRow = (id: GridRowId) => {
    if (isNewRow(id)) {
      removeRow(id);
    }
    cancelRowMode(id);
  };

  const deleteRow = (id: GridRowId) => {
    removeRow(id);
    updateRowMode(GridRowModes.View, id);
  };

  const saveRow = (id: GridRowId) => {
    if (isNewRow(id)) {
      removeIsNewFlag(id);
    }
    updateRowMode(GridRowModes.View, id);
  };

  return {
    addRow,
    editRow,
    cancelRow,
    deleteRow,
    saveRow,
    rowModesModel,
    setRowModesModel,
  };
};
